const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export type CyclePattern = '2:1' | '3:1' | '4:1';

export interface AthleteAgeInput {
    age?: number | null;
    birthdate?: string | null;
}

export function normalizeBirthdate(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (!ISO_DATE_REGEX.test(trimmed)) return undefined;

    const [yearStr, monthStr, dayStr] = trimmed.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);

    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return undefined;
    }

    const candidate = new Date(Date.UTC(year, month - 1, day));
    if (
        Number.isNaN(candidate.getTime()) ||
        candidate.getUTCFullYear() !== year ||
        candidate.getUTCMonth() !== month - 1 ||
        candidate.getUTCDate() !== day
    ) {
        return undefined;
    }

    return trimmed;
}

export function calculateAgeFromBirthdate(birthdate?: string | null, today: Date = new Date()): number | undefined {
    const normalized = normalizeBirthdate(birthdate);
    if (!normalized) return undefined;

    const [yearStr, monthStr, dayStr] = normalized.split('-');
    const birthYear = Number(yearStr);
    const birthMonth = Number(monthStr);
    const birthDay = Number(dayStr);

    if (!Number.isInteger(birthYear) || !Number.isInteger(birthMonth) || !Number.isInteger(birthDay)) {
        return undefined;
    }

    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();

    let age = currentYear - birthYear;
    const hasHadBirthdayThisYear =
        currentMonth > birthMonth || (currentMonth === birthMonth && currentDay >= birthDay);

    if (!hasHadBirthdayThisYear) age -= 1;
    if (age < 0 || age > 120) return undefined;

    return age;
}

export function resolveAthleteAge(input: AthleteAgeInput): number | undefined {
    const derivedAge = calculateAgeFromBirthdate(input.birthdate);
    if (derivedAge !== undefined) return derivedAge;

    if (typeof input.age === 'number' && Number.isFinite(input.age)) {
        const rounded = Math.floor(input.age);
        return rounded >= 0 && rounded <= 120 ? rounded : undefined;
    }

    return undefined;
}

export function resolveCyclePatternForAthlete(
    input: AthleteAgeInput,
    fallback: CyclePattern = '3:1'
): CyclePattern {
    const age = resolveAthleteAge(input);
    if (age !== undefined && age >= 50) return '2:1';
    return fallback;
}
